import React, { Component } from 'react';
import { bool, func, object, node, number, shape, string, arrayOf } from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { createResourceLocatorString } from '../../../util/routes';

import { ModalInMobile, Button } from '../../../components';

import PopupOpenerButton from '../PopupOpenerButton/PopupOpenerButton';
import css from './SearchFiltersMobile.module.css';

const BOTTOM_SHEET_MODAL_ID = 'SearchFiltersMobile.bottomSheet';

// Filter icon SVG for the drawer trigger button
const FilterIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 4h16M4 9h10M7 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

class SearchFiltersMobileComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isFiltersOpenOnMobile: false,
      initialQueryParams: null,
      activeFilterIndex: 0,
      localQueryParams: {},
      sessionKey: 0,
      activePanelInvalid: false,
    };

    // Set by the currently visible panel (so far only the price range
    // filter) via `registerFlush`, so "Aplicar filtros" can force its
    // pending debounced update to apply before reading localQueryParams.
    this.activePanelFlush = null;

    this.openFilters = this.openFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.closeFilters = this.closeFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.applyFilters = this.applyFilters.bind(this);
    this.resetFilters = this.resetFilters.bind(this);
    this.getLocalHandleChangedValueFn = this.getLocalHandleChangedValueFn.bind(this);
    this.handleActivePanelValidityChange = this.handleActivePanelValidityChange.bind(this);
    this.handleActivePanelFlushRegister = this.handleActivePanelFlushRegister.bind(this);
  }

  componentWillUnmount() {
    const { isBottomSheetVariant, onManageDisableScrolling } = this.props;
    if (isBottomSheetVariant && this.state.isFiltersOpenOnMobile) {
      onManageDisableScrolling(BOTTOM_SHEET_MODAL_ID, false);
    }
  }

  // Open filters modal, set the initial parameters to current ones
  openFilters() {
    const { onOpenModal, onManageDisableScrolling, isBottomSheetVariant, urlQueryParams } = this.props;
    onOpenModal();
    if (isBottomSheetVariant) {
      onManageDisableScrolling(BOTTOM_SHEET_MODAL_ID, true);
    }
    this.setState(prev => ({
      isFiltersOpenOnMobile: true,
      initialQueryParams: urlQueryParams,
      localQueryParams: { ...urlQueryParams },
      sessionKey: prev.sessionKey + 1,
      activePanelInvalid: false,
    }));
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    const {
      isBottomSheetVariant,
      onCloseModal,
      onManageDisableScrolling,
      history,
      routeConfiguration,
      routeName = 'SearchPage',
    } = this.props;

    if (isBottomSheetVariant) {
      // Deferred mode: URL was never changed, just close and discard local state
      onManageDisableScrolling(BOTTOM_SHEET_MODAL_ID, false);
      onCloseModal();
      this.setState({ isFiltersOpenOnMobile: false });
      return;
    }

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, {}, this.state.initialQueryParams)
    );
    onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false, initialQueryParams: null });
  }

  // Close the filter modal
  closeFilters() {
    this.props.onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false });
  }

  // Reset all filter query parameters (regular modal / sidebar)
  resetAll(e) {
    this.props.resetAll(e);
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  // Bottom-sheet: apply pending local params to URL and close
  applyFilters() {
    const { history, routeName, routeConfiguration, onCloseModal, onManageDisableScrolling } = this.props;

    const pushAndClose = () => {
      const { page: _page, ...params } = this.state.localQueryParams;
      history.push(
        createResourceLocatorString(routeName || 'SearchPage', routeConfiguration, {}, params)
      );
      onManageDisableScrolling(BOTTOM_SHEET_MODAL_ID, false);
      onCloseModal();
      this.setState({ isFiltersOpenOnMobile: false });
    };

    if (this.activePanelFlush) {
      // Forces the price filter's debounced onSubmit (if a change is still
      // pending) to run now instead of up to 400ms later. Without this, a
      // range reset to default right before tapping Apply could push the
      // stale (pre-reset) price to the URL instead of clearing it.
      this.activePanelFlush();
    }
    // The flush above (if any) updates localQueryParams via this.setState,
    // which batches with this one — so pushAndClose only runs, and only
    // reads state, after that update has actually committed.
    this.setState(prevState => prevState, pushAndClose);
  }

  // Bottom-sheet: clear filter params from local buffer only (no URL push, sheet stays open)
  resetFilters(e) {
    if (e && e.currentTarget) e.currentTarget.blur();
    const clearedKeys = ['keywords', 'address', 'bounds', 'origin'];
    const preserved = Object.keys(this.state.localQueryParams)
      .filter(key => !key.startsWith('pub_') && !key.startsWith('meta_') && !clearedKeys.includes(key))
      .reduce((acc, key) => ({ ...acc, [key]: this.state.localQueryParams[key] }), {});
    this.setState(prev => ({ localQueryParams: preserved, sessionKey: prev.sessionKey + 1 }));
  }

  // Tracks whether the currently visible panel (the price range field, so
  // far the only one that reports this) holds an invalid pending value, so
  // the "Aplicar filtros" button can be disabled instead of silently
  // discarding the draft.
  handleActivePanelValidityChange(isInvalid) {
    this.setState({ activePanelInvalid: isInvalid });
  }

  // Registered/cleared by the currently visible panel (see IntegerRangeFilter)
  handleActivePanelFlushRegister(flushFn) {
    this.activePanelFlush = flushFn;
  }

  // Updates local (pending) query params without touching the URL
  getLocalHandleChangedValueFn(_useHistoryPush) {
    return updatedURLParams => {
      this.setState(prevState => {
        const next = { ...prevState.localQueryParams };
        Object.entries(updatedURLParams || {}).forEach(([key, val]) => {
          if (val === null || val === undefined) {
            delete next[key];
          } else {
            next[key] = val;
          }
        });
        return { localQueryParams: next };
      });
    };
  }

  render() {
    const {
      rootClassName,
      className,
      children,
      filterItems,
      sortByComponent,
      favoritesButton,
      listingsAreLoaded,
      resultsCount,
      searchInProgress,
      showAsModalMaxWidth,
      onMapIconClick,
      onManageDisableScrolling,
      selectedFiltersCount,
      noResultsInfo,
      intl,
      isMapVariant,
      isDrawerVariant,
      isBottomSheetVariant,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    const resultsFound = (
      <FormattedMessage id="SearchFiltersMobile.foundResults" values={{ count: resultsCount }} />
    );
    const noResults = <FormattedMessage id="SearchFiltersMobile.noResults" />;
    const loadingResults = <FormattedMessage id="SearchFiltersMobile.loadingResults" />;
    const filtersHeading = intl.formatMessage({ id: 'SearchFiltersMobile.heading' });
    const modalCloseButtonMessage = intl.formatMessage({ id: 'SearchFiltersMobile.cancel' });

    const showListingsLabel = intl.formatMessage(
      { id: 'SearchFiltersMobile.showListings' },
      { count: resultsCount }
    );

    // Bottom sheet with two-panel layout (Flash Search mobile)
    if (isBottomSheetVariant) {
      const isOpen = this.state.isFiltersOpenOnMobile;
      const activeIdx = this.state.activeFilterIndex || 0;
      const { localQueryParams, sessionKey } = this.state;
      const items = filterItems || [];

      // Count active (pending) selections per filter for left-nav badges.
      // The location filter is special-cased since it doesn't have a
      // filterConfig (it isn't a listing-field/default filter) — its pending
      // state lives under "bounds" in the local buffer rather than a
      // pub_/meta_-prefixed key. ("address" itself never reaches here — it's
      // stripped out wherever query params get read back from the URL, since
      // it's treated as display-only text for the search bar — so "bounds" is
      // what actually reflects whether a location is applied.)
      const getNavBadgeCount = (filterConfig, itemId) => {
        if (itemId === 'location') {
          return localQueryParams?.bounds ? 1 : 0;
        }
        if (!filterConfig) return 0;
        const scope = filterConfig.scope || 'public';
        const prefix = scope === 'meta' ? 'meta_' : 'pub_';
        const paramKey = `${prefix}${filterConfig.key}`;
        const value = localQueryParams[paramKey];
        if (!value) return 0;
        if (filterConfig.schemaType === 'multi-enum') {
          const cleaned = String(value).replace(/^has_(any|all):/, '');
          return cleaned.split(',').filter(Boolean).length;
        }
        return 1;
      };

      const hasActiveFilters = items.some(item => getNavBadgeCount(item.config, item.id) > 0);

      return (
        <div className={classes}>
          {/* Trigger row: result count + Filtro button + Sort by */}
          <div className={css.bottomSheetTopBar}>
            <span className={css.searchResultSummary}>
              {listingsAreLoaded && resultsCount > 0 ? resultsFound : null}
              {listingsAreLoaded && resultsCount === 0 ? noResults : null}
              {searchInProgress ? loadingResults : null}
            </span>
            <div className={css.bottomSheetActions}>
              <button
                className={classNames(css.filtroButton, {
                  [css.filtroButtonActive]: selectedFiltersCount > 0,
                })}
                onClick={this.openFilters}
                type="button"
              >
                <FilterIcon />
                <FormattedMessage id="SearchFiltersMobile.filterLabel" />
                {selectedFiltersCount > 0 && (
                  <span className={css.drawerBadge}>{selectedFiltersCount}</span>
                )}
              </button>
              {favoritesButton}
              {sortByComponent}
            </div>
          </div>

          {noResultsInfo || null}

          {/* Backdrop */}
          {isOpen && <div className={css.sheetBackdrop} onClick={this.cancelFilters} />}

          {/* Bottom sheet panel */}
          <div className={classNames(css.bottomSheet, { [css.bottomSheetOpen]: isOpen })}>
            {/* Header */}
            <div className={css.sheetHeader}>
              <span className={css.sheetTitle}>Filtros</span>
              <button className={css.sheetClose} onClick={this.cancelFilters} type="button">
                ✕
              </button>
            </div>

            {/* Two-panel body */}
            <div className={css.sheetBody}>
              {/* Left nav: filter category names with active-count badge */}
              <nav className={css.sheetNav}>
                {items.map((item, idx) => {
                  const badgeCount = getNavBadgeCount(item.config, item.id);
                  return (
                    <button
                      key={item.id}
                      className={classNames(css.sheetNavItem, {
                        [css.sheetNavItemActive]: idx === activeIdx,
                      })}
                      onClick={() =>
                        this.setState({ activeFilterIndex: idx, activePanelInvalid: false })
                      }
                      type="button"
                    >
                      <span className={css.sheetNavItemLabel}>{item.label}</span>
                      {badgeCount > 0 && (
                        <span className={css.sheetNavSelectionText}>
                          <FormattedMessage id="SearchFiltersMobile.selectedCount" values={{ count: badgeCount }} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>

              {/* Right content: rendered with local (pending) params */}
              {/* key=sessionKey remounts filters each time sheet opens so form state is fresh */}
              <div className={css.sheetContent} key={sessionKey}>
                {items[activeIdx]?.renderContent?.({
                  localQueryParams,
                  getHandleChangedValueFn: this.getLocalHandleChangedValueFn,
                  onValidityChange: this.handleActivePanelValidityChange,
                  registerFlush: this.handleActivePanelFlushRegister,
                }) || null}
              </div>
            </div>

            {/* Footer: reset (disabled when nothing selected) + apply */}
            <div className={css.sheetFooter}>
              <button
                className={classNames(css.sheetResetButton, {
                  [css.sheetResetButtonDisabled]: !hasActiveFilters,
                })}
                onClick={e => this.resetFilters(e)}
                disabled={!hasActiveFilters}
                type="button"
              >
                <FormattedMessage id="SearchFiltersMobile.resetAll" />
              </button>
              <Button
                className={css.showListingsButton}
                onClick={this.applyFilters}
                disabled={this.state.activePanelInvalid}
              >
                <FormattedMessage id="SearchFiltersMobile.applyFilters" />
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Left-slide drawer variant used on Flash Search (mobile only)
    if (isDrawerVariant) {
      const isOpen = this.state.isFiltersOpenOnMobile;
      return (
        <div className={classes}>
          <div className={css.drawerTopBar}>
            <div className={css.searchResultSummary}>
              {listingsAreLoaded && resultsCount > 0 ? resultsFound : null}
              {listingsAreLoaded && resultsCount === 0 ? noResults : null}
              {searchInProgress ? loadingResults : null}
            </div>
            <button
              className={classNames(css.drawerTrigger, { [css.drawerTriggerActive]: selectedFiltersCount > 0 })}
              onClick={this.openFilters}
            >
              <FilterIcon />
              <FormattedMessage id="SearchFiltersMobile.filtersButtonLabel" />
              {selectedFiltersCount > 0 && (
                <span className={css.drawerBadge}>{selectedFiltersCount}</span>
              )}
            </button>
            {sortByComponent}
          </div>

          {noResultsInfo ? noResultsInfo : null}

          {/* Backdrop */}
          {isOpen && (
            <div className={css.drawerBackdrop} onClick={this.cancelFilters} />
          )}

          {/* Slide-in panel */}
          <div className={classNames(css.drawerPanel, { [css.drawerPanelOpen]: isOpen })}>
            <div className={css.drawerHeader}>
              <span className={css.modalHeading}>{filtersHeading}</span>
              <button className={css.resetAllButton} onClick={e => this.resetAll(e)}>
                <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
              </button>
            </div>
            <div className={css.drawerFiltersWrapper}>{children}</div>
            <div className={css.drawerFooter}>
              <Button className={css.showListingsButton} onClick={this.closeFilters}>
                {showListingsLabel}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={classes}>
        <div className={css.searchResultSummary}>
          {listingsAreLoaded && resultsCount > 0 ? resultsFound : null}
          {listingsAreLoaded && resultsCount === 0 ? noResults : null}
          {searchInProgress ? loadingResults : null}
        </div>
        <div className={css.buttons}>
          <PopupOpenerButton isSelected={selectedFiltersCount > 0} toggleOpen={this.openFilters}>
            <FormattedMessage
              id="SearchFiltersMobile.filtersButtonLabel"
              className={css.mapIconText}
            />
          </PopupOpenerButton>

          {sortByComponent}
          {isMapVariant ? (
            <div className={css.mapIcon} onClick={onMapIconClick}>
              <FormattedMessage id="SearchFiltersMobile.openMapView" className={css.mapIconText} />
            </div>
          ) : null}
          {favoritesButton}
        </div>

        {noResultsInfo ? noResultsInfo : null}

        <ModalInMobile
          id="SearchFiltersMobile.filters"
          isModalOpenOnMobile={this.state.isFiltersOpenOnMobile}
          onClose={this.cancelFilters}
          showAsModalMaxWidth={showAsModalMaxWidth}
          onManageDisableScrolling={onManageDisableScrolling}
          containerClassName={css.modalContainer}
          closeButtonMessage={modalCloseButtonMessage}
        >
          <div className={css.modalHeadingWrapper}>
            <span className={css.modalHeading}>{filtersHeading}</span>
            <button className={css.resetAllButton} onClick={e => this.resetAll(e)}>
              <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
            </button>
          </div>
          {this.state.isFiltersOpenOnMobile ? (
            <div className={css.filtersWrapper}>{children}</div>
          ) : null}

          <div className={css.showListingsContainer}>
            <Button className={css.showListingsButton} onClick={this.closeFilters}>
              {showListingsLabel}
            </Button>
          </div>
        </ModalInMobile>
      </div>
    );
  }
}

SearchFiltersMobileComponent.defaultProps = {
  rootClassName: null,
  className: null,
  sortByComponent: null,
  favoritesButton: null,
  resultsCount: null,
  searchInProgress: false,
  selectedFiltersCount: 0,
  isMapVariant: true,
  onMapIconClick: () => {},
  routeName: 'SearchPage',
  isDrawerVariant: false,
  isBottomSheetVariant: false,
  filterItems: [],
};

SearchFiltersMobileComponent.propTypes = {
  rootClassName: string,
  className: string,
  urlQueryParams: object.isRequired,
  sortByComponent: node,
  favoritesButton: node,
  filterItems: arrayOf(shape({ id: string, label: string, config: object, renderContent: func })),
  listingsAreLoaded: bool.isRequired,
  resultsCount: number,
  searchInProgress: bool,
  showAsModalMaxWidth: number.isRequired,
  onMapIconClick: func,
  onManageDisableScrolling: func.isRequired,
  onOpenModal: func.isRequired,
  onCloseModal: func.isRequired,
  resetAll: func.isRequired,
  selectedFiltersCount: number,
  isMapVariant: bool,
  routeName: string,
  isDrawerVariant: bool,
  isBottomSheetVariant: bool,

  // from useIntl
  intl: intlShape.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

const SearchFiltersMobile = props => {
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();

  return (
    <SearchFiltersMobileComponent
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      {...props}
    />
  );
};

export default SearchFiltersMobile;
